/** Overall work is a weighted estimate, never elapsed time or a time remaining
 * prediction. Counts within each stage describe actual completed work. */
export type ProjectLoadStage =
  | 'reading'
  | 'decoding'
  | 'hydrating'
  | 'templates'
  | 'graphs'
  | 'preparing';

export interface ProjectLoadProgress {
  stage: ProjectLoadStage;
  completed: number;
  total: number;
  fraction: number;
}
export type ProjectLoadObserver = (progress: ProjectLoadProgress) => void;
/** Completed units at an existing work boundary, not a duration estimate. */
export type ProjectLoadWorkObserver = (completed: number, total: number) => void;
export type ProjectLoadReport = (
  stage: ProjectLoadStage,
  completed?: number,
  total?: number,
) => void;

const ranges: Record<ProjectLoadStage, readonly [number, number]> = {
  reading: [0, 0.08],
  decoding: [0.08, 0.3],
  hydrating: [0.3, 0.55],
  templates: [0.55, 0.7],
  graphs: [0.7, 0.93],
  preparing: [0.93, 0.97],
};

/** Only stage/twelfth-of-work changes reach observers. The six-segment UI can
 * ignore intermediate bins. Use existing batch boundaries, without polling
 * timers or per-tile notifications. */
export function createProjectLoadReporter(
  observer?: ProjectLoadObserver,
): ProjectLoadReport {
  let fraction = 0;
  let lastStage: ProjectLoadStage | undefined;
  let lastCell = -1;
  return (stage, completed = 0, total = 1) => {
    const [start, end] = ranges[stage];
    // Migration can decode old bytes before reading the upgraded container.
    // Do not move the presented stage backwards when that nested read reports.
    if (lastStage && start < ranges[lastStage][0]) return;
    completed = Number.isFinite(completed) ? Math.max(0, completed) : 0;
    total = Number.isFinite(total) ? Math.max(0, total) : 1;
    const local = total > 0 ? Math.min(1, completed / total) : 1;
    fraction = Math.max(fraction, start + (end - start) * local);
    const cell = Math.floor(fraction * 12);
    if (stage === lastStage && cell === lastCell) return;
    lastStage = stage;
    lastCell = cell;
    try {
      observer?.({ stage, completed, total, fraction });
    } catch {
      // A presentation observer must never change loading or migration outcomes.
    }
  };
}
